import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Lock, Mail, Unlock, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { binder, documentTypes, type BinderStatus, type DocumentVersionRow } from '@/api/client';
import {
  InitiateApprovalDialog,
  type InitiateApprovalWorkflowName,
} from './InitiateApprovalDialog';
import { ApprovalProgress } from './ApprovalProgress';
import { UnlockDocumentDialog } from './UnlockDocumentDialog';
import { errorText } from '@/lib/permissionError';

/**
 * Sprint-15 (option 2) — roles allowed to perform an exceptional unlock
 * per client spec §7.2. Mirrors the BE allowlist in unlockDocument.ts.
 */
const UNLOCK_ROLES = new Set([
  'platform_admin',
  'application_admin',
  'site_admin',
  'regulatory_specialist',
]);

/**
 * Sprint-9 — Per-version lifecycle action buttons.
 *
 * Renders only the legal transitions for `version.status`, posts via
 * `binder.transitionVersionStatus`, then invalidates both the per-document
 * version list AND the folder's slot list (the slot mirrors the current
 * version's status, so it must be refreshed too).
 *
 * Re-opening a "current" version to "draft" requires a change-reason payload
 * (Requirements doc — IRB-traceable lifecycle edits); the ChangeReasonModal
 * below collects the standardized reason + free-text details before posting.
 */

export interface VersionStatusActionsProps {
  binderId: string;
  documentId: string;
  version: DocumentVersionRow;
  isDocLocked: boolean;
  documentTypeName?: string;
}

interface TransitionDef {
  label: string;
  target: BinderStatus;
  requires_change_reason?: boolean;
}

/**
 * Statuses for which we surface the "Send for Approval" button. Steady
 * states (current/superseded/archived) intentionally omit it — those versions
 * are either already promoted or terminal, so kicking off a new envelope from
 * them would never be valid.
 */
const DOCUSEAL_ENABLED_STATUSES: ReadonlySet<BinderStatus> = new Set([
  'draft',
  'preview',
  'in_approval',
]);

/** Single hardcoded workflow — the eSignature approval envelope. */
const ESIGNATURE_WORKFLOW: InitiateApprovalWorkflowName = 'Send for eSignature Approval';

/** Mirrors services/eisf/signatureExemptTypes.ts — sponsor-issued, filed unaltered. */
const SIGNATURE_EXEMPT_CODES = new Set([
  'protocol',
  'protocol-amendment',
  'protocol-synopsis',
  'protocol-summary-of-changes',
  'protocol-clarification',
]);

function transitionsFor(
  status: BinderStatus,
  signatureExempt: boolean,
): readonly TransitionDef[] {
  switch (status) {
    case 'draft':
      // "Send for Approval" is NOT a bare state transition — it goes through
      // the workflow-picker dropdown so the correct DocuSeal envelope + role-
      // resolved approver routing is spawned atomically (initiateApproval
      // service transitions status→in_approval internally). Only "Mark as
      // Preview" (lightweight internal review, no routing) is a plain
      // transition. Named "Mark as Preview" to disambiguate from the
      // "Send for Review" DocuSeal workflow inside the dropdown, which spawns
      // a routed reviewer envelope.
      return [
        { label: 'Mark as Preview', target: 'preview' },
        ...(signatureExempt
          ? [{ label: 'Submit for Approval', target: 'in_approval' as BinderStatus }]
          : []),
        { label: 'Discard', target: 'archived', requires_change_reason: true },
      ];
    case 'preview':
      return [
        { label: 'Back to Draft', target: 'draft' },
        ...(signatureExempt
          ? [{ label: 'Submit for Approval', target: 'in_approval' as BinderStatus }]
          : []),
        { label: 'Discard', target: 'archived', requires_change_reason: true },
      ];
    case 'in_approval':
      return [
        { label: 'Approve & Set Current', target: 'current' },
        { label: 'Reject', target: 'draft' },
      ];
    case 'current':
      return [
        { label: 'Re-open for Edit', target: 'draft', requires_change_reason: true },
        { label: 'Archive', target: 'archived' },
      ];
    case 'superseded':
      return [{ label: 'Archive', target: 'archived' }];
    case 'archived':
    default:
      return [];
  }
}

export function VersionStatusActions({
  binderId,
  documentId,
  version,
  isDocLocked,
  documentTypeName,
}: VersionStatusActionsProps) {
  const qc = useQueryClient();
  const [reasonModal, setReasonModal] = useState<TransitionDef | null>(null);
  const [activeWorkflow, setActiveWorkflow] = useState<InitiateApprovalWorkflowName | null>(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const { user } = useAuth();
  const canUnlock = !!user && UNLOCK_ROLES.has(user.role);

  const catalogQ = useQuery({
    queryKey: ['document-types', 'list'],
    queryFn: () => documentTypes.list(),
    staleTime: 10 * 60_000,
    enabled: DOCUSEAL_ENABLED_STATUSES.has(version.status) && !!documentTypeName,
  });
  const docTypeCode = (catalogQ.data?.document_types ?? []).find(
    (t) => t.document_type_name === documentTypeName,
  )?.document_type_code;
  const approversQ = useQuery({
    queryKey: ['document-type-approvers', docTypeCode],
    queryFn: () => documentTypes.getApprovers(docTypeCode!),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: !!docTypeCode,
  });
  const approvalResolved = !!documentTypeName && !catalogQ.isLoading
    && (!docTypeCode || !approversQ.isLoading);
  const approverCount = approversQ.data?.approvers.length ?? 0;
  const isExempt = !!docTypeCode && SIGNATURE_EXEMPT_CODES.has(docTypeCode);
  const canBeApproved = !!docTypeCode && approverCount > 0 && !isExempt;
  const transitions = transitionsFor(version.status, isExempt);

  const showDocusealMenu = DOCUSEAL_ENABLED_STATUSES.has(version.status)
    && (!documentTypeName || !approvalResolved || canBeApproved);
  const approvalBlockedReason = isExempt
    ? `"${documentTypeName}" is issued by the sponsor and filed unaltered, so it is not routed `
      + 'for signature. Approve it by setting the version to Current.'
    : !docTypeCode
    ? `"${documentTypeName}" is not in the eISF Catalog, so it has no approval rule.`
    : `No approvers are set for "${documentTypeName}" in the eISF Catalog.`;
  const envelopeInFlight = version.docuseal_submission_id !== null;
  const isSigned = version.signed_at !== null;

  const transitionMut = useMutation({
    mutationFn: (payload: {
      target: BinderStatus;
      change_reason?: string;
      change_reason_details?: string;
    }) =>
      binder.transitionVersionStatus(binderId, documentId, version.version_id, {
        target_status: payload.target,
        ...(payload.change_reason ? { change_reason: payload.change_reason } : {}),
        ...(payload.change_reason_details
          ? { change_reason_details: payload.change_reason_details }
          : {}),
      }),
    onSuccess: (res) => {
      toast.success(
        `Version v${res.version.version_label} → ${res.version.status.replace('_', ' ')}`,
      );
      qc.invalidateQueries({ queryKey: ['binder-versions', binderId, documentId] });
      // The folder slot list mirrors current_version.status, so any transition
      // can change what the slot renders. Use prefix matching to refresh every
      // folder's document list for this binder.
      qc.invalidateQueries({ queryKey: ['binder', binderId] });
      // This component is also rendered inside DocumentViewerModal's Footer —
      // that modal reads its own separate document-preview query, which
      // otherwise keeps showing the pre-transition status/signed_at until
      // the modal is closed and reopened.
      qc.invalidateQueries({ queryKey: ['document-preview', binderId, documentId] });
      setReasonModal(null);
    },
    onError: (err) => {
      toast.error(errorText(err, 'Transition failed'));
    },
  });

  // We still render even when there are no transitions if the version is
  // either Signed or has an envelope in flight, so the badges remain visible
  // on archived/superseded rows.
  if (transitions.length === 0 && !showDocusealMenu && !isSigned) {
    return null;
  }

  function handleClick(t: TransitionDef) {
    if (transitionMut.isPending) return;
    if (t.requires_change_reason) {
      setReasonModal(t);
      return;
    }
    transitionMut.mutate({ target: t.target });
  }

  function handleReasonSubmit(reason: string, details: string) {
    if (!reasonModal || transitionMut.isPending) return;
    transitionMut.mutate({
      target: reasonModal.target,
      change_reason: reason,
      change_reason_details: details,
    });
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {isSigned && (
          <span
            className="badge badge-success inline-flex items-center gap-1 text-[10px]"
            title={`Signed at ${version.signed_at}`}
          >
            <CheckCircle2 className="h-3 w-3" />
            Signed
          </span>
        )}
        {isDocLocked && (
          <span
            className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700"
            title="Locked. In-place edits blocked; replace via supersession or use exceptional unlock."
          >
            <Lock className="h-3 w-3" />
            Locked
          </span>
        )}
        {isDocLocked && canUnlock && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100"
            onClick={() => setUnlockOpen(true)}
            title="Exceptional unlock — requires justification. Audited."
          >
            <Unlock className="h-3 w-3" />
            Unlock…
          </button>
        )}

        {transitions.map((t) => (
          <button
            key={`${t.target}:${t.label}`}
            type="button"
            className="btn-secondary text-xs"
            onClick={() => handleClick(t)}
            disabled={isDocLocked || transitionMut.isPending}
            title={
              isDocLocked ? 'Document is locked.' : `Transition to "${t.target.replace('_', ' ')}"`
            }
          >
            {transitionMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {t.label}
          </button>
        ))}

        {!showDocusealMenu
          && DOCUSEAL_ENABLED_STATUSES.has(version.status)
          && approvalResolved && (
            <span
              className="badge inline-flex items-center gap-1 text-[10px] text-ink-muted"
              title={`${approvalBlockedReason} Set approvers on the document type to enable approval.`}
            >
              <Mail className="h-3 w-3" />
              {isExempt ? 'Signed off in the audit trail' : 'No approval rule'}
            </span>
          )}

        {showDocusealMenu &&
          (envelopeInFlight ? (
            <span
              className="badge badge-info inline-flex items-center gap-1 text-[10px]"
              title={`DocuSeal submission id: ${version.docuseal_submission_id}`}
            >
              <Mail className="h-3 w-3" />
              Envelope in progress · ID {(version.docuseal_submission_id ?? '').slice(0, 8)}
            </span>
          ) : (
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-1 text-xs"
              onClick={() => setActiveWorkflow(ESIGNATURE_WORKFLOW)}
              disabled={isDocLocked || transitionMut.isPending}
              title={
                isDocLocked
                  ? 'Document is locked.'
                  : 'Route this version to the configured approvers and generate the e-signature envelope.'
              }
            >
              <Mail className="h-3 w-3" />
              Send for Approval
            </button>
          ))}
      </div>

      <ChangeReasonModal
        open={reasonModal !== null}
        targetStatus={reasonModal?.target ?? ''}
        pending={transitionMut.isPending}
        onClose={() => {
          if (!transitionMut.isPending) setReasonModal(null);
        }}
        onSubmit={handleReasonSubmit}
      />

      {/* Sprint-15: live multi-approver routing state for in_approval versions */}
      {version.status === 'in_approval' && (
        <div className="mt-2">
          <ApprovalProgress
            binderId={binderId}
            documentId={documentId}
            versionId={version.version_id}
          />
        </div>
      )}

      {activeWorkflow !== null && (
        <InitiateApprovalDialog
          binderId={binderId}
          documentId={documentId}
          version={version}
          workflowName={activeWorkflow}
          open={activeWorkflow !== null}
          onClose={() => setActiveWorkflow(null)}
          onInitiated={() => setActiveWorkflow(null)}
        />
      )}

      <UnlockDocumentDialog
        binderId={binderId}
        documentId={documentId}
        documentName={version.file_name}
        open={unlockOpen}
        onClose={() => setUnlockOpen(false)}
        onUnlocked={() => setUnlockOpen(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// ChangeReasonModal — captures the Requirements-doc change reason + details
// ---------------------------------------------------------------------------

const STANDARD_REASONS: readonly string[] = [
  'New version issued',
  'Information change',
  'Correction',
  'IRB revision',
  'Sponsor request',
  'Other',
];

interface ChangeReasonModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string, details: string) => void;
  targetStatus: string;
  pending?: boolean;
}

function ChangeReasonModal({
  open,
  onClose,
  onSubmit,
  targetStatus,
  pending = false,
}: ChangeReasonModalProps) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');

  // Reset whenever the modal re-opens.
  useEffect(() => {
    if (open) {
      setReason('');
      setDetails('');
    }
  }, [open]);

  if (!open) return null;

  const trimmedDetails = details.trim();
  const isValid = reason !== '' && trimmedDetails.length >= 4;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid || pending) return;
    onSubmit(reason, trimmedDetails);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
      onClick={(e) => {
        if (e.target === e.currentTarget && !pending) onClose();
      }}
    >
      <form onSubmit={handleSubmit} className="card w-full max-w-lg space-y-4 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary">Change reason required</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Re-opening a current version to{' '}
              <span className="font-medium text-ink-primary">{targetStatus.replace('_', ' ')}</span>{' '}
              must be justified for the audit trail.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="label text-xs" htmlFor="change-reason">
            Reason *
          </label>
          <select
            id="change-reason"
            className="input"
            value={reason}
            disabled={pending}
            onChange={(e) => setReason(e.target.value)}
            required
          >
            <option value="" disabled>
              Select a reason…
            </option>
            {STANDARD_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label text-xs" htmlFor="change-details">
            Details *
          </label>
          <textarea
            id="change-details"
            className="input min-h-[88px]"
            value={details}
            disabled={pending}
            placeholder="Briefly describe the change (minimum 4 characters)."
            onChange={(e) => setDetails(e.target.value)}
            required
          />
          <p className="mt-1 text-[11px] text-ink-muted">
            {trimmedDetails.length < 4
              ? `${4 - trimmedDetails.length} more character${4 - trimmedDetails.length === 1 ? '' : 's'} required.`
              : 'Looks good.'}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || pending}>
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Submit
          </button>
        </div>
      </form>
    </div>
  );
}
